import { useEffect, useState } from 'react';

import { useSelectedAppContext } from '../../../contexts/SelectedAppContext';
import {
  DevHubBox,
  DevHubButton,
  DevHubGrid,
  DevHubTypography,
} from '@devxdiscover/devhub-ui';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { CSVDownload } from 'react-csv';
import { useAppBulkEditContext } from './context/AppBulkEditContext';
import { useMutation } from '@apollo/client';
import AppBulkUpdateStatus from './AppBulkUpdateStatus';
import {
  BaseMutationResult,
  MutationStatus,
} from './types/updateApplication/BaseApplicationProperty';
import { SelectFieldOptions } from './types/appBulkEditTypes';

type AppBulkEditReviewProps = {
  toggleSubmit: boolean;
};

/**
 * Component for reviewing bulk edits of applications.
 * @param props - The properties for the component.
 * @returns The rendered component.
 */
const AppBulkEditReview = ({
  toggleSubmit,
}: AppBulkEditReviewProps): JSX.Element => {
  const { selectedApps } = useSelectedAppContext();
  const { field, selectedRole, property } = useAppBulkEditContext();

  const [toggleExport, setToggleExport] = useState(false);
  const [mutationStatus, setMutationStatus] = useState<MutationStatus[]>([]);

  const inputVar = property.propertyMutationInput();
  const mutationQuery = property.mutationQuery();
  const [multipleUpdates, { data, loading, error }] =
    useMutation<BaseMutationResult>(mutationQuery);

  useEffect(() => {
    const input = selectedApps.map(app => {
      return {
        applicationId: app.applicationId,
        ...inputVar,
      };
    });
    if (toggleSubmit) {
      multipleUpdates({ variables: { input: input } });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toggleSubmit, field]);

  const handleExportClick = () => {
    if (selectedApps.length > 0) {
      setToggleExport(true);
    }
    setTimeout(() => {
      setToggleExport(false);
    }, 1000);
  };
  useEffect(() => {
    if (!loading && data) {
      const result = property.getMutationResult(data);
      setMutationStatus(result);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, loading]);

  const renderSelectedApps = () => {
    return selectedApps.map(app => {
      /// needs to be fixed, based on the output from fusion
      function getStatus() {
        const isMutationStarted = toggleSubmit ? 'Pending' : 'Not started';
        const currentStatus = mutationStatus?.find(
          status => status.applicationId === app.applicationId,
        );
        return currentStatus?.success ? 'Success' : isMutationStarted;
      }
      return {
        applicationId: app.applicationId,
        name: app.name,
        role: selectedRole.displayName,
        currentValue:
          (typeof property.getCurrentDisplayValue === 'function' &&
            property?.getCurrentDisplayValue(app)) ||
          '-',
        newValue: property.getNewDisplayValue(),
        status: getStatus(),
      };
    });
  };

  const cSVHeaders = () => {
    const headers = [
      { label: 'Application Id', key: 'applicationId' },
      { label: 'Name', key: 'name' },
      { label: 'Role', key: 'role' },
      { label: 'Current Value', key: 'currentValue' },
      { label: 'New Value', key: 'newValue' },
      { label: 'Status', key: 'status' },
    ];
    return field !== SelectFieldOptions.ROLES
      ? headers.filter(item => item.key !== 'role')
      : headers;
  };

  return (
    <DevHubBox>
      <DevHubGrid container justifyContent="space-between" padding="small">
        <DevHubTypography fontSize="large">
          Review update for {field} change
        </DevHubTypography>
        <DevHubButton
          variant="contained"
          color="primary"
          onClick={handleExportClick}
        >
          Generate CSV
        </DevHubButton>
      </DevHubGrid>
      {toggleExport && (
        <CSVDownload
          headers={cSVHeaders()}
          filename="AppBulkEdit.csv"
          data={renderSelectedApps()}
          target="_blank"
        />
      )}
      <DataTable
        value={renderSelectedApps()}
        paginator={false}
        rows={10}
        dataKey="applicationId"
      >
        <Column field="applicationId" header="Application Id" />
        <Column field="name" header="Name" />
        {field === SelectFieldOptions.ROLES && (
          <Column field="role" header="Role" />
        )}
        <Column field="currentValue" header={`Current ${property.label}`} />
        <Column field="newValue" header={`New ${property.label}`} />
        {toggleSubmit && (
          <Column
            field="status"
            header="Status"
            body={rowData => (
              <AppBulkUpdateStatus
                applicationIds={selectedApps.map(app => app.applicationId)}
                applicationId={rowData.applicationId}
                toggleSubmit={toggleSubmit}
                mutationStatus={mutationStatus}
                loading={loading}
                error={error}
              />
            )}
          />
        )}
      </DataTable>
    </DevHubBox>
  );
};

export default AppBulkEditReview;
